import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Typography,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  TextField,
  Chip,
  Stack,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material'
import Search from '@mui/icons-material/Search'
import Close from '@mui/icons-material/Close'
import { PMPSectionInputLabel } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { fetchOptions, lookupOptionsByCodes } from 'src/mentor/services/emrOptionsApi'

/**
 * HybridSelectField - Renders HYBRID type fields with pills UI
 * UI Pattern:
 * - COMMON TOOLS: Checkboxes (horizontal with wrap)
 * - FIND MORE TOOLS: Search input + result pills
 * - CHOSEN: Selected items as removable chips
 *
 * @param {object} field - EmrFieldDef with optionSource.type === "HYBRID"
 * @param {string|string[]} value - Current value (code or array of codes)
 * @param {function} onChange - (code, display) => void for SELECT_ONE
 * @param {function} onChangeMulti - (codes, items) => void for SELECT_MULTI
 * @param {boolean} isMulti - Whether this is a multi-select field
 */
export default function HybridSelectField({ field, value, onChange, onChangeMulti, isMulti }) {
  const [searchText, setSearchText] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchData, setSearchResults] = useState([])
  const selectedOptions = useMemo(() => {
    if (isMulti) {
      return value?.map((val) => ({ value: val?.code, label: val?.display })) || []
    } else {
      return value ? [{ value: value?.code, label: value?.display }] : []
    }
  }, [isMulti, value])
  // const selectedOptions = (value || [])?.map((val) => ({
  //   value: val?.code,
  //   label: val?.display,
  // }))
  const searchResults = (searchData || [])?.filter(
    (result) => !selectedOptions.some((opt) => opt.value === result.value),
  )

  const [initialPillsLoaded, setInitialPillsLoaded] = useState(false)

  // Track loaded codes to prevent unnecessary re-fetches
  const loadedCodesRef = useRef(new Set())

  const optionSource = field?.optionSource
  const staticOptions = useMemo(() => optionSource?.staticOptions || [], [optionSource])

  // Get selected codes (for multi-select) or single code (for select-one)
  const selectedCodes = useMemo(() => {
    if (isMulti) {
      return Array.isArray(value) ? value : []
    }
    return value ? [value] : []
  }, [value, isMulti])

  // Load initial pills on mount (show some default options)
  useEffect(() => {
    const loadInitialPills = async () => {
      if (initialPillsLoaded || !optionSource?.queryName) {
        return
      }

      setIsSearching(true)
      try {
        // Fetch initial options with empty search (to show popular/recent options)
        const results = await fetchOptions(
          optionSource?.queryName,
          '', // Empty search to get initial results
          optionSource?.params || {},
          10, // Load 20 initial pills
        )

        // Filter out staticOptions (already shown as checkboxes)
        const staticCodes = new Set(staticOptions.map((opt) => opt.code))
        const filtered = results.filter((r) => !staticCodes.has(r.value))

        setSearchResults(filtered)
        setInitialPillsLoaded(true)
      } catch (error) {
        console.error('[HybridSelectField] Error loading initial pills:', error)
      } finally {
        setIsSearching(false)
      }
    }

    loadInitialPills()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionSource?.queryName, optionSource?.params])

  const loadInitialValues = async () => {
    // if (selectedCodes.length === 0 || !optionSource?.queryName) {
    //   loadedCodesRef.current.clear()
    //   return
    // }
    // try {
    //   // Check if all selected codes are in static options
    //   let options = []
    //   // if (codesToFetch.length > 0) {
    //   const lookupResults = await lookupOptionsByCodes(
    //     optionSource.queryName,
    //     [...selectedCodes],
    //     optionSource.params || {},
    //   )
    //   options = [...options, ...lookupResults]
    //   return
    //   // setSelectedOptions(options)
    // } catch (error) {
    //   console.error('[HybridSelectField] Error loading initial values:', error)
    // }
  }

  // Load initial selected values on mount or when value changes
  useEffect(() => {
    loadInitialValues()
  }, [])

  // Debounced search
  useEffect(() => {
    if (!searchText) {
      // If search is empty, don't search (keep initial pills visible)
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const results = await fetchOptions(
          optionSource?.queryName,
          searchText,
          optionSource?.params || {},
          10,
        )

        // Filter out already selected and staticOptions
        const staticCodes = new Set(staticOptions.map((opt) => opt.code))
        const filtered = results.filter(
          (r) => !selectedCodes.includes(r.value) && !staticCodes.has(r.value),
        )

        setSearchResults(filtered)
      } catch (error) {
        console.error('[HybridSelectField] Search error:', error)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchText, selectedCodes, optionSource, staticOptions])

  // Handle clicking a pill from search results (adds to selection)
  const handlePillClick = (code, display) => {
    // Mark this code as loaded to prevent re-fetch
    loadedCodesRef.current.add(code)

    if (isMulti) {
      const newCodes = [...selectedCodes, code]
      const newOptions = [...selectedOptions, { value: code, label: display }]
      const newItems = newOptions.map((opt) => ({
        code: opt.value,
        display: opt.label,
      }))

      if (onChangeMulti) {
        onChangeMulti(newCodes, newItems)
      }
    } else {
      // SELECT_ONE: replace selection
      if (onChange) {
        onChange(code, display)
      }
    }

    // Clear search text only (keep results visible)
    setSearchText('')
  }

  // Handle removing a selected item (from CHOSEN section)
  const handleRemove = (code) => {
    if (isMulti) {
      const newCodes = selectedCodes.filter((c) => c !== code)
      const newOptions = selectedOptions.filter((opt) => opt.value !== code)
      const newItems = newOptions.map((opt) => ({
        code: opt.value,
        display: opt.label,
      }))

      if (onChangeMulti) {
        onChangeMulti(newCodes, newItems)
      }
    } else {
      // SELECT_ONE: clear selection
      if (onChange) {
        onChange(null, null)
      }
    }
  }

  // Handle checkbox toggle (SELECT_MULTI only)
  const handleCheckboxChange = (optionCode, optionDisplay, isChecked) => {
    if (isChecked) {
      handlePillClick(optionCode, optionDisplay)
    } else {
      handleRemove(optionCode)
    }
  }

  // Handle radio change (SELECT_ONE only)
  const handleRadioChange = (event) => {
    const optionCode = event.target.value
    const option = staticOptions.find((opt) => opt.code === optionCode)

    if (option) {
      if (onChange) {
        onChange(option.code, option.display)
      }
    }
  }

  console.log({ selectedCodes, staticOptions, selectedOptions })

  return (
    <div
      key={field.code}
      style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}
    >
      <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>

      {/* COMMON TOOLS Section (checkboxes or radios) */}
      {staticOptions.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '11px',
              letterSpacing: '0.5px',
              mb: 1,
              display: 'block',
            }}
          >
            Common Tools
          </Typography>

          {isMulti ? (
            // Checkboxes for SELECT_MULTI (horizontal with wrap)
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '12px' }}>
              {staticOptions.map((option) => (
                <FormControlLabel
                  key={option.code}
                  control={
                    <Checkbox
                      checked={selectedCodes
                        ?.map((selectedOption) => selectedOption?.code)
                        .includes(option.code)}
                      onChange={(e) =>
                        handleCheckboxChange(option.code, option.display, e.target.checked)
                      }
                      disabled={field.readOnly}
                      size="small"
                    />
                  }
                  label={option.display || option.code}
                  sx={{ margin: 0 }}
                />
              ))}
            </Box>
          ) : (
            // Radio buttons for SELECT_ONE (horizontal with wrap)
            <FormControl component="fieldset">
              <RadioGroup
                value={selectedOptions?.[0]?.value}
                onChange={handleRadioChange}
                sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '12px' }}
              >
                {staticOptions.map((option) => (
                  <FormControlLabel
                    key={option.code}
                    value={option.code}
                    control={<Radio size="small" disabled={field.readOnly} />}
                    label={option.display || option.code}
                    sx={{ margin: 0 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </Box>
      )}

      {/* FIND MORE TOOLS Section (search + pills) */}
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '11px',
            letterSpacing: '0.5px',
            mb: 1,
            display: 'block',
          }}
        >
          Find More Tools
        </Typography>

        {/* Search Input */}
        <TextField
          fullWidth
          size="small"
          placeholder={field.ui?.searchPlaceholder || field.placeholder || 'Search assessments...'}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          disabled={field.readOnly}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText('')}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Search Results Pills */}
        {isSearching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {searchResults.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
            {searchResults.map((result) => (
              <Chip
                key={result.value}
                label={result.label}
                onClick={() => handlePillClick(result.value, result.label)}
                clickable
                disabled={field.readOnly}
                size="medium"
                sx={{
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  },
                }}
              />
            ))}
          </Stack>
        )}

        {!isSearching && searchText && searchText.length >= 2 && searchResults.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            No results found for &quot;{searchText}&quot;
          </Typography>
        )}
        {/* CHOSEN Section - Selected items displayed right below search */}
        {selectedOptions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 600,
                fontSize: '11px',
                letterSpacing: '0.5px',
                mb: 1,
                display: 'block',
              }}
            >
              Chosen
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {selectedOptions.map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  onDelete={field.readOnly ? undefined : () => handleRemove(opt.value)}
                  color="primary"
                  size="medium"
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Help Text */}
      {field.helpText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {field.helpText}
        </Typography>
      )}
    </div>
  )
}
